import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiContext } from '@/contexts/apiContext';
import SettingsSection from '@/components/entities/configuration/settingsSection';
import SettingsValidator from '@/components/entities/settingsValidators';

type EntityMissingSettings = {
  itemIndex: number;
  entityName: string;
  entityType: string;
  entityDefId: string;
  /** Only the sections that have at least one missing required visible field */
  missingSections: any[];
  /** Full settingDefs from the entity def (for rendering) */
  allSections: any[];
  /** Original settings from the backend response */
  originalSettings: Record<string, any>;
};

type Props = {
  surveyId: string;
  surveyResponseId: string;
  /** Called whenever any setting value changes, so parent can update before Create pipeline */
  onSettingsChange: (itemIndex: number, entityDefId: string, updatedSettings: Record<string, any>) => void;
  /** The settings keyed by itemIndex that the parent is tracking */
  itemSettings: Record<number, Record<string, any>>;
};

const settingsValidator = new SettingsValidator();

/**
 * Displays any required settings that are missing after survey processing,
 * allowing the user to fill them inline before creating the pipeline.
 */
const MissingSettingsPanel = ({ surveyId, surveyResponseId, onSettingsChange, itemSettings }: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [entityMissingSettings, setEntityMissingSettings] = useState<EntityMissingSettings[]>([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!surveyId || !surveyResponseId) return;

    const fetchAndValidate = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await apiContext.getUserSurveyProcessingResult(surveyId, surveyResponseId);
        const items: any[] = result?.items || [];

        const missingList: EntityMissingSettings[] = [];

        for (let idx = 0; idx < items.length; idx++) {
          const item = items[idx];
          const { entityType, entityDefId, settings = {}, name } = item;

          const entityDef = item.dataSourceDef || item.agentDef || item.userAppDef;
          if (!entityDef) continue;

          const settingDefs: any[] = entityDef?.settingDefs || [];
          if (settingDefs.length === 0) continue;

          // Current settings = item settings merged with any parent-tracked updates
          const currentSettings = { ...settings, ...(itemSettings[idx] || {}) };

          // Find sections that have at least one missing required visible setting
          const missingSections: any[] = [];
          for (const section of settingDefs) {
            const missingItems = (section.items || []).filter((sItem: any) => {
              if (!settingsValidator.isItemVisible(sItem, currentSettings)) return false;
              if (!sItem.required) return false;

              // If the setting has a default value, it is not "missing" (it will use the default)
              const hasDefault =
                sItem.defaultValue !== undefined && sItem.defaultValue !== null && sItem.defaultValue !== '';
              if (hasDefault) return false;

              const val = currentSettings[sItem.name];
              // empty string, null, undefined, or empty array counts as missing
              if (val === undefined || val === null || val === '') return true;
              if (Array.isArray(val) && val.length === 0) return true;
              return false;
            });
            if (missingItems.length > 0) {
              missingSections.push({ ...section, items: missingItems });
            }
          }

          if (missingSections.length > 0) {
            missingList.push({
              itemIndex: idx,
              entityName: name || entityDef?.name || entityDefId,
              entityType,
              entityDefId,
              missingSections,
              allSections: settingDefs,
              originalSettings: settings,
            });
          }
        }

        setEntityMissingSettings(missingList);
        setInitialized(true);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAndValidate();
  }, [surveyId, surveyResponseId]);

  // Re-evaluate which settings are still missing as the user fills them in
  const getMissingSectionsForEntity = (entry: EntityMissingSettings): any[] => {
    const currentSettings = {
      ...(entry.originalSettings || {}),
      ...(itemSettings[entry.itemIndex] || {}),
    };
    return entry.allSections
      .map((section: any) => {
        const missingItems = (section.items || []).filter((sItem: any) => {
          if (!settingsValidator.isItemVisible(sItem, currentSettings)) return false;
          if (!sItem.required) return false;

          // If the setting has a default value, it is not "missing" (it will use the default)
          const hasDefault = sItem.defaultValue !== undefined && sItem.defaultValue !== null && sItem.defaultValue !== '';
          if (hasDefault) return false;

          const val = currentSettings[sItem.name];
          if (val === undefined || val === null || val === '') return true;
          if (Array.isArray(val) && val.length === 0) return true;
          return false;
        });
        return missingItems.length > 0 ? { ...section, items: missingItems } : null;
      })
      .filter(Boolean);
  };

  const entityTypeLabel = (entityType: string) => {
    if (entityType === 'dataSourceInstance') return 'Data Source';
    if (entityType === 'agentInstance') return 'Agent';
    if (entityType === 'userAppInstance') return 'App';
    return entityType;
  };

  if (loading) {
    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
        <CircularProgress size={16} color="secondary" />
        <Typography variant="caption" color="text.secondary">
          Checking required settings…
        </Typography>
      </Stack>
    );
  }

  if (error) {
    return null; // silently skip — don't block the user
  }

  if (!initialized) return null;

  const totalMissing = entityMissingSettings.reduce((sum, e) => {
    return sum + getMissingSectionsForEntity(e).reduce((s2, sec) => s2 + sec.items.length, 0);
  }, 0);

  return (
    <Box>
      {totalMissing === 0 ? (
        <Alert
          severity="success"
          icon={<CheckCircleOutlineIcon fontSize="small" />}
          sx={{ mb: 1.5, borderRadius: '10px', fontSize: '0.8rem' }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            All required settings are configured.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            You can review or adjust your configuration below before creating the pipeline.
          </Typography>
        </Alert>
      ) : (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon fontSize="small" />}
          sx={{ mb: 1.5, borderRadius: '10px', fontSize: '0.8rem' }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {totalMissing} required setting{totalMissing !== 1 ? 's' : ''} need{totalMissing === 1 ? 's' : ''} to be
            configured before your pipeline will function correctly.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            You can fill them in below or configure them later from the dashboard.
          </Typography>
        </Alert>
      )}

      <Stack spacing={1}>
        {entityMissingSettings.map((entry) => {
          const currentSections = entry.missingSections;
          if (currentSections.length === 0) return null;

          const currentSettings = {
            ...(entry.originalSettings || {}),
            ...(itemSettings[entry.itemIndex] || {}),
          };
          const missCount = getMissingSectionsForEntity(entry).reduce((s, sec) => s + sec.items.length, 0);

          const handleSettingChange = (settingName: string, value: any) => {
            const updated = { ...currentSettings, [settingName]: value };
            onSettingsChange(entry.itemIndex, entry.entityDefId, updated);
          };

          return (
            <Accordion
              key={entry.itemIndex}
              defaultExpanded
              disableGutters
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'warning.light',
                borderRadius: '10px !important',
                '&:before': { display: 'none' },
                bgcolor: alpha(theme.palette.warning.light, 0.06),
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ minHeight: 40, py: 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {entry.entityName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      bgcolor: alpha(theme.palette.warning.main, 0.15),
                      color: 'warning.dark',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: '6px',
                      fontWeight: 600,
                    }}
                  >
                    {entityTypeLabel(entry.entityType)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {missCount} missing field{missCount !== 1 ? 's' : ''}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Divider sx={{ mb: 1.5 }} />
                <Stack spacing={1.5}>
                  {currentSections.map((section: any, sIdx: number) => (
                    <Box key={sIdx}>
                      {section.sectionName && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}
                        >
                          {section.sectionName}
                        </Typography>
                      )}
                      <SettingsSection
                        entityDefId={entry.entityDefId}
                        settings={currentSettings}
                        settingsSection={section}
                        errors={{}}
                        onChange={handleSettingChange}
                      />
                    </Box>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>
    </Box>
  );
};

export default MissingSettingsPanel;
